import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import { useStrikeStatus } from "@/hooks/useStrikeStatus";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";
import { useIpfsUpload } from "@/hooks/useIpfsUpload";
import { useAirdropChallenge } from "@/hooks/useAirdropChallenge";

import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import gameBg from "@/assets/game-bg.png";

import { NFTCreationWarning, AntiFraudCheckbox } from "@/components/StrikeBanner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { Upload, Image, Loader2, AlertTriangle, Check, ExternalLink } from "lucide-react";

const PAGE_BG_STYLE: React.CSSProperties = {
  backgroundImage: `linear-gradient(hsl(var(--background) / 0.35), hsl(var(--background) / 0.45)), url(${gameBg})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundAttachment: "fixed",
};

type Profile = {
  id: string;
  display_name: string;
  avatar_url: string | null;
};

const MAX_DESCRIPTION_LENGTH = 100;
const MAX_COLLECTION_LENGTH = 50;

export default function Mint() {
  const navigate = useNavigate();
  const { publicKey, connected } = useWalletSafe();
  const { status: sessionStatus } = useWalletSupabaseSession();
  const { strikeStatus, combinedStatus } = useStrikeStatus();
  const { getComplianceData } = useDeviceFingerprint();
  const { uploadToIpfs, uploading: uploadingToIpfs, progress: ipfsProgress } = useIpfsUpload();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const [newNFTName, setNewNFTName] = useState("");
  const [newNFTDescription, setNewNFTDescription] = useState("");
  const [newNFTCollection, setNewNFTCollection] = useState("");
  const [newNFTFile, setNewNFTFile] = useState<File | null>(null);
  const [creatingNFT, setCreatingNFT] = useState(false);
  const [antiFraudAgreed, setAntiFraudAgreed] = useState(false);

  const [lastMintedNft, setLastMintedNft] = useState<{ id: string; name: string; image_url: string | null } | null>(null);
  const [shareCompleted, setShareCompleted] = useState(false);

  const walletAddress = publicKey?.toBase58() || "";
  const shortWallet = walletAddress.slice(-5);

  const isBanned = combinedStatus?.isAnyBanned ?? strikeStatus?.is_banned ?? false;

  const { registerContestMint } = useAirdropChallenge(profile?.id, walletAddress);

  const generateDisplayName = useCallback((wallet: string) => wallet.slice(-5).toUpperCase(), []);

  // Restore pending share state after returning from X
  useEffect(() => {
    const pendingData = localStorage.getItem("flow_share_pending_nft");
    if (!pendingData) return;
    try {
      const nftData = JSON.parse(pendingData);
      setLastMintedNft(nftData);
      setShareCompleted(false);
      toast({ title: "Welcome back! Please complete the share on X." });
    } catch {
      localStorage.removeItem("flow_share_pending_nft");
    }
  }, []);

  useEffect(() => {
    if (!connected || !publicKey) {
      setLoading(false);
      return;
    }
    if (sessionStatus !== "valid") {
      setLoading(true);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }
        const wallet = publicKey.toBase58();
        const defaultName = generateDisplayName(wallet);
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, display_name, avatar_url")
          .eq("id", user.id)
          .maybeSingle();
        if (profileData) {
          setProfile({
            id: profileData.id,
            display_name: profileData.display_name || defaultName,
            avatar_url: profileData.avatar_url,
          });
        } else {
          setProfile({ id: user.id, display_name: defaultName, avatar_url: null });
        }
      } catch (e) {
        console.error("Mint init error:", e);
      }
      setLoading(false);
    })();
  }, [connected, publicKey, sessionStatus, generateDisplayName]);

  const logTransaction = async (
    userId: string, wallet: string, action: string, entityType?: string, entityId?: string, details?: Record<string, unknown>,
  ) => {
    try {
      const complianceData = getComplianceData();
      const logData: any = { user_id: userId, wallet_address: wallet, action_type: action };
      if (entityType) logData.entity_type = entityType;
      if (entityId) logData.entity_id = entityId;
      logData.details = { ...(details || {}), _compliance: complianceData };
      await supabase.from("transaction_logs").insert(logData);
    } catch (e) {
      console.error("Log transaction error:", e);
    }
  };

  const createNFT = async () => {
    if (!profile || !newNFTFile) {
      toast({ title: "Please select an image for your NFT", variant: "destructive" });
      return;
    }
    if (isBanned) {
      toast({ title: "Account Suspended", description: "You cannot create NFTs while your account is suspended.", variant: "destructive" });
      return;
    }

    setCreatingNFT(true);
    try {
      const nameToUse = newNFTName.trim() || `${profile.display_name}'s NFT`;
      const uploadResult = await uploadToIpfs(newNFTFile);
      if (!uploadResult.success || !uploadResult.url) throw new Error(uploadResult.error || 'Failed to upload to IPFS');
      const publicUrl = uploadResult.url;

      const initialPriceUsd = 1.0;
      const { data: newNFT, error: insertError } = await supabase
        .from("nfts")
        .insert({
          name: nameToUse,
          description: newNFTDescription.trim() || null,
          collection_name: newNFTCollection.trim() || null,
          image_url: publicUrl,
          creator_wallet: walletAddress,
          creator_name: profile.display_name,
          owner_id: profile.id,
          owner_wallet: walletAddress,
          owner_name: profile.display_name,
          price: initialPriceUsd,
          current_price: initialPriceUsd,
          base_price: initialPriceUsd,
          total_earned: 0,
          sales_count: 0,
          is_listed: true,
          image_verified: false,
          image_check_status: 'pending',
        })
        .select("id, name, image_url")
        .single();

      if (insertError) throw insertError;

      if (newNFT) {
        await supabase.from("nft_ownership_history").insert({
          nft_id: newNFT.id,
          from_wallet: null,
          to_wallet: walletAddress,
          price: 0,
          transaction_type: 'mint',
        });
        await logTransaction(profile.id, walletAddress, 'nft_create', 'nft', newNFT.id, { name: nameToUse, image_url: publicUrl });
        supabase.functions.invoke('verify-nft-image', {
          body: { nft_id: newNFT.id, image_url: publicUrl },
        }).then(({ error }) => { if (error) console.error("Image verification error:", error); });

        setLastMintedNft({ id: newNFT.id, name: newNFT.name, image_url: newNFT.image_url });
        setShareCompleted(false);
        registerContestMint(newNFT.id).catch(console.error);
      }

      setNewNFTFile(null);
      setNewNFTName("");
      setNewNFTDescription("");
      setNewNFTCollection("");
      toast({ title: "NFT created on IPFS! Image verification in progress..." });
    } catch (error) {
      console.error("Create NFT error:", error);
      toast({ title: "Failed to create NFT", variant: "destructive" });
    }
    setCreatingNFT(false);
  };

  return (
    <div className="min-h-screen text-foreground" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="mint" />

      <main className="container mx-auto px-4 pt-32 pb-12 max-w-2xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">Mint NFT</h1>
          <p className="text-sm text-muted-foreground mt-1">Create your NFT in seconds. Your first mint is free.</p>
        </div>

        {!connected ? (
          <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
            <CardContent className="py-12 text-center space-y-3">
              <Image className="w-12 h-12 mx-auto text-muted-foreground/60" />
              <p className="text-foreground font-medium">Connect your wallet to mint an NFT</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <Card className="border-border/50 bg-card/80 backdrop-blur-sm">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/50 bg-card/80 backdrop-blur-sm overflow-hidden">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Mint New NFT
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">NFT Image</label>
                <label className="flex items-center justify-center gap-2 h-10 px-4 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground truncate">
                    {newNFTFile ? newNFTFile.name : "Choose image"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setNewNFTFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  NFT Name <span className="text-muted-foreground">(optional)</span>
                </label>
                <Input
                  value={newNFTName}
                  onChange={(e) => setNewNFTName(e.target.value)}
                  placeholder={`${profile?.display_name || shortWallet}'s NFT`}
                  className="bg-background/50"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Description <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <span className={`text-xs ${newNFTDescription.length > MAX_DESCRIPTION_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {newNFTDescription.length}/{MAX_DESCRIPTION_LENGTH}
                  </span>
                </div>
                <Input
                  value={newNFTDescription}
                  onChange={(e) => setNewNFTDescription(e.target.value)}
                  placeholder="Short description..."
                  className={`bg-background/50 ${newNFTDescription.length > MAX_DESCRIPTION_LENGTH ? 'border-destructive' : ''}`}
                  maxLength={MAX_DESCRIPTION_LENGTH + 10}
                />
                {newNFTDescription.length > MAX_DESCRIPTION_LENGTH && (
                  <p className="text-xs text-destructive">Description too long. Max {MAX_DESCRIPTION_LENGTH} characters.</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Collection <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <span className={`text-xs ${newNFTCollection.length > MAX_COLLECTION_LENGTH ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {newNFTCollection.length}/{MAX_COLLECTION_LENGTH}
                  </span>
                </div>
                <Input
                  value={newNFTCollection}
                  onChange={(e) => setNewNFTCollection(e.target.value)}
                  placeholder="e.g. My Art Series"
                  className={`bg-background/50 ${newNFTCollection.length > MAX_COLLECTION_LENGTH ? 'border-destructive' : ''}`}
                  maxLength={MAX_COLLECTION_LENGTH + 5}
                />
                {newNFTCollection.length > MAX_COLLECTION_LENGTH && (
                  <p className="text-xs text-destructive">Collection name too long. Max {MAX_COLLECTION_LENGTH} characters.</p>
                )}
              </div>

              <NFTCreationWarning />

              <AntiFraudCheckbox checked={antiFraudAgreed} onCheckedChange={setAntiFraudAgreed} />

              {uploadingToIpfs && (
                <div className="w-full space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Uploading to IPFS...</span>
                    <span className="text-primary font-medium">{ipfsProgress}%</span>
                  </div>
                  <Progress value={ipfsProgress} className="h-2" />
                </div>
              )}

              <Button
                onClick={createNFT}
                disabled={creatingNFT || uploadingToIpfs || !newNFTFile || isBanned || newNFTDescription.length > MAX_DESCRIPTION_LENGTH || newNFTCollection.length > MAX_COLLECTION_LENGTH || !antiFraudAgreed}
                className="w-full"
              >
                {creatingNFT || uploadingToIpfs ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{uploadingToIpfs ? 'Uploading to IPFS...' : 'Creating...'}</>
                ) : isBanned ? (
                  <><AlertTriangle className="w-4 h-4 mr-2" />Suspended</>
                ) : !antiFraudAgreed ? (
                  <><AlertTriangle className="w-4 h-4 mr-2" />Agree to Policy</>
                ) : (
                  <><Image className="w-4 h-4 mr-2" />Mint for Free</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />

      {/* Post-Mint Share Modal — Mandatory */}
      <Dialog open={!!lastMintedNft} onOpenChange={() => { /* prevent dismiss */ }}>
        <DialogContent className="sm:max-w-md [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              NFT Created Successfully!
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-primary">Sharing on X is required</span> to keep your free mint. Share your creation to unlock it!
            </DialogDescription>
          </DialogHeader>
          {lastMintedNft && (
            <div className="flex flex-col items-center gap-4">
              {lastMintedNft.image_url && (
                <img
                  src={lastMintedNft.image_url}
                  alt={lastMintedNft.name}
                  className="w-32 h-32 rounded-lg object-cover border border-border"
                />
              )}
              <p className="text-sm font-medium text-foreground">{lastMintedNft.name}</p>

              {!shareCompleted ? (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    const tweetText = `I just minted "${lastMintedNft.name}" on @FLOWnft_org\n\nCheck it out`;
                    const shareUrl = `https://flownft.org/share/nft/${lastMintedNft.id}`;
                    const returnUrl = `${window.location.origin}/mint`;
                    localStorage.setItem("flow_share_pending_nft", JSON.stringify(lastMintedNft));
                    const bounceUrl = `/x-share-bounce?tweet=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}&name=${encodeURIComponent(lastMintedNft.name)}&return_url=${encodeURIComponent(returnUrl)}`;
                    window.location.assign(bounceUrl);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Share on X (Required)
                </Button>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-center justify-center gap-2 text-green-500 text-sm font-medium py-2">
                    <Check className="w-4 h-4" />
                    Share completed! Your NFT is live.
                  </div>
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => {
                      localStorage.removeItem("flow_share_pending_nft");
                      setLastMintedNft(null);
                      setShareCompleted(false);
                      navigate("/dashboard");
                    }}
                  >
                    Done
                  </Button>
                </div>
              )}

              {!shareCompleted && (
                <p className="text-xs text-muted-foreground text-center">
                  After sharing on X, come back here and confirm.
                </p>
              )}

              {!shareCompleted && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground text-xs"
                  onClick={() => {
                    localStorage.removeItem("flow_share_pending_nft");
                    setShareCompleted(true);
                  }}
                >
                  I've already shared ✓
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
